import dayjs from 'dayjs';
import partition from 'lodash/partition';
import { appConfig } from 'config/app';
import {
  CalculatorSettingKey,
  Days,
  GracePeriodTimeWorked,
  ICalculatorParticipant,
  ICalculatorRule,
  ICalculatorRulesSource,
  ICalculatorSettingsOverride,
  ICategoryGroup,
  ICurrentCalculatorSettings,
  IDaySegment,
  IDaySegmentGracePeriodPolicy,
  IIntegration,
  IIntegrationConfig,
  IPaymentSetCalculations,
  IPaymentSetCalculationsSettings,
  IPaymentSetDaySegment,
  IPOSIntegration,
  IPOSResults,
  ISettingDifference,
  ISettingsComparison,
  SettingChangeType,
  SettingsGateState,
} from 'types';
import { isProduction } from './app';

/**
 * Mirrors `account_services` `DEFAULT_GRACE_PERIOD_POLICY` (`grace_period_policy_service.ts:10`).
 * A snapshot or a site with no policy configured was calculated with these values, so the comparison
 * must treat them as equal to an absent policy.
 */
const DEFAULT_GRACE_PERIOD_POLICY: IDaySegmentGracePeriodPolicy = {
  segmentStart: appConfig.TIP_CALCULATOR.DEFAULT_GRACE_PERIOD_MINUTES,
  segmentEnd: appConfig.TIP_CALCULATOR.DEFAULT_GRACE_PERIOD_MINUTES,
  timeWorked: GracePeriodTimeWorked.EXCLUDE,
};

/**
 * Incidental metadata that differs between the two sides without meaning anything to a calculation.
 *
 * Stripped recursively **on purpose**: the sides are assembled by different services, so a Mongo
 * subdocument id can appear on a nested share or beneficiary on one side and not the other. The cost
 * is that a meaningful nested `id` would be ignored too — if one is added, compare it explicitly.
 */
const IGNORED_COMPARISON_KEYS = new Set(['_id', 'id', 'createdAt', 'updatedAt']);

/** Rule fields the comparison is defined on. Anything outside this list is incidental. */
const COMPARED_RULE_KEYS: (keyof ICalculatorRule)[] = ['contributor', 'additionalContributors', 'value', 'sharePolicy', 'includeNoLabor', 'isVirtual'];

const CONFIG_PUBLIC_PERMISSION = 'public';

/**
 * Recursively drops incidental metadata and `undefined` members, and sorts object keys so two
 * equivalent objects serialize identically. Array order is preserved — the two sides receive their
 * nested arrays (shares, beneficiaries) from the same source, so a reorder there is a real change.
 */
const normalizeForComparison = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(normalizeForComparison);
  if (value === null || typeof value !== 'object') return value;

  return Object.keys(value as Record<string, unknown>)
    .filter((key) => !IGNORED_COMPARISON_KEYS.has(key) && (value as Record<string, unknown>)[key] !== undefined)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = normalizeForComparison((value as Record<string, unknown>)[key]);
      return acc;
    }, {});
};

const serializeForComparison = (value: unknown): string => JSON.stringify(normalizeForComparison(value));

/**
 * A rule reduced to the fields the comparison is defined on. Left un-normalized: every consumer
 * either serializes it or emits it as a difference payload, and both normalize on the way.
 */
const pickComparedRuleFields = (rule: ICalculatorRule): Record<string, unknown> =>
  COMPARED_RULE_KEYS.filter((key) => rule[key] !== undefined).reduce<Record<string, unknown>>((acc, key) => {
    acc[key] = rule[key];
    return acc;
  }, {});

/** Rules are matched by their contributor, the only stable identity a rule has once `_id` is stripped. */
const ruleContributorKey = (rule: ICalculatorRule): string => `${rule.contributor?.type || ''}:${rule.contributor?.name || ''}`;

const groupBy = <T>(items: T[], key: (item: T) => string): Map<string, T[]> =>
  items.reduce<Map<string, T[]>>((acc, item) => {
    const group = acc.get(key(item)) || [];
    acc.set(key(item), [...group, item]);
    return acc;
  }, new Map());

interface IKeyedDiffOptions<T> {
  setting: CalculatorSettingKey;
  original: T[];
  current: T[];
  /** The identity two entries must share to be compared against each other rather than reported as added/removed. */
  key: (item: T) => string;
  label: (item: T, key: string) => string;
  /** The value equality is decided on. Omit to report presence only (added/removed, never modified). */
  value?: (items: T[]) => unknown;
}

/**
 * Compares two sides keyed by identity: matched keys whose value differs are MODIFIED, unmatched
 * keys are ADDED (current only) or REMOVED (snapshot only).
 *
 * Always `replayable`, which is a claim about the override bundle rather than an oversight: it carries
 * `rules`, `categoryGroups` and `participants` wholesale, so anything since removed is restored by
 * sending the snapshot's own list. Integration config is the exception and is diffed separately.
 *
 * Payloads carry the entries themselves, never the composite match key — that would read as noise.
 */
const diffByKey = <T>({ setting, original, current, key, label, value }: IKeyedDiffOptions<T>): ISettingDifference[] => {
  const originalGroups = groupBy(original, key);
  const currentGroups = groupBy(current, key);
  const payload = (items: T[]): unknown => normalizeForComparison(value ? value(items) : items);

  return Array.from(new Set([...originalGroups.keys(), ...currentGroups.keys()]))
    .sort()
    .reduce<ISettingDifference[]>((acc, groupKey) => {
      const originalGroup = originalGroups.get(groupKey);
      const currentGroup = currentGroups.get(groupKey);

      if (originalGroup && !currentGroup) {
        return [...acc, { setting, changeType: SettingChangeType.REMOVED, label: label(originalGroup[0], groupKey), original: payload(originalGroup), replayable: true }];
      }
      if (currentGroup && !originalGroup) {
        return [...acc, { setting, changeType: SettingChangeType.ADDED, label: label(currentGroup[0], groupKey), current: payload(currentGroup), replayable: true }];
      }
      // Without a value accessor the key encodes everything compared, so a matched key cannot differ.
      if (!value || serializeForComparison(value(originalGroup)) === serializeForComparison(value(currentGroup))) return acc;

      return [...acc, { setting, changeType: SettingChangeType.MODIFIED, label: label(currentGroup[0], groupKey), original: payload(originalGroup), current: payload(currentGroup), replayable: true }];
    }, []);
};

/** A day segment window. `name` is display-only: renaming a segment changes no calculation, so it takes no part in equality. */
const daySegmentWindowKey = (segment: IPaymentSetDaySegment | IDaySegment): string => `${segment.start}|${segment.end}|${Boolean(segment.endsNextDay)}`;

const daySegmentLabel = (segment: IPaymentSetDaySegment | IDaySegment): string => `${segment.name} (${segment.start}–${segment.end})`;

/**
 * The site segments that applied on a `YYYY-MM-DD` business date.
 *
 * `account_services` filters a site's segments by the date's weekday before every calculation
 * (`day_segment.ts:170-175`), and the snapshot stores only that filtered set. Filtering the current
 * side the same way keeps a site whose segments vary by weekday from reporting a difference on every
 * date. An unparseable date filters nothing rather than everything — a noisier comparison beats
 * reporting every segment as removed.
 */
const daySegmentsForDate = (daySegments: IDaySegment[], businessDate: string): IDaySegment[] => {
  const date = dayjs(businessDate);
  if (!date.isValid()) return daySegments;
  const dayName = date.format('dddd').toUpperCase() as Days;

  // A segment with no days recorded is compared rather than dropped — dropping would read as a removal.
  return daySegments.filter((segment) => !segment.days?.length || segment.days.includes(dayName));
};

/**
 * Day segments compare as an unordered set of windows, so an added segment shows up as an extra
 * element and a removed one as a missing element. A retimed segment appears as both, which is
 * correct without an identity key — the snapshot deliberately stores no segment id.
 */
const diffDaySegments = (original: IPaymentSetDaySegment[], current: IDaySegment[]): ISettingDifference[] => {
  const toWindow = (segment: IPaymentSetDaySegment | IDaySegment) => ({ start: segment.start, end: segment.end, endsNextDay: Boolean(segment.endsNextDay) });
  const originalWindows = groupBy(original, daySegmentWindowKey);
  const currentWindows = groupBy(current, daySegmentWindowKey);

  return Array.from(new Set([...originalWindows.keys(), ...currentWindows.keys()]))
    .sort()
    .reduce<ISettingDifference[]>((acc, windowKey) => {
      const originalGroup = originalWindows.get(windowKey) || [];
      const currentGroup = currentWindows.get(windowKey) || [];
      // Compare occurrence counts so a duplicated window is reported rather than collapsed.
      const removed = originalGroup.slice(currentGroup.length).map(
        (segment): ISettingDifference => ({
          setting: CalculatorSettingKey.DAY_SEGMENTS,
          changeType: SettingChangeType.REMOVED,
          label: daySegmentLabel(segment),
          original: toWindow(segment),
          replayable: true,
        })
      );
      const added = currentGroup.slice(originalGroup.length).map(
        (segment): ISettingDifference => ({
          setting: CalculatorSettingKey.DAY_SEGMENTS,
          changeType: SettingChangeType.ADDED,
          label: daySegmentLabel(segment),
          current: toWindow(segment),
          replayable: true,
        })
      );

      return [...acc, ...removed, ...added];
    }, []);
};

/**
 * Config as a `key -> value` map.
 *
 * `permission` is **absent at runtime** even though `IIntegrationConfig` declares it:
 * `PosIntegrationConfigResponse` does not expose it (`account_services/src/app/api/response.ts:5042`)
 * and `ApiResponse.toJSON` serializes with `excludeExtraneousValues`. So an absent `permission` must
 * count as public — requiring `'public'` would drop every entry and silently turn this comparison into
 * a no-op. The filter only exists to drop an explicitly non-public entry, should one ever be sent.
 */
const publicConfigMap = (config: IIntegrationConfig[] = []): Map<string, string> =>
  config.reduce((acc, { key, value, permission }) => {
    if (permission === undefined || permission === CONFIG_PUBLIC_PERMISSION) acc.set(key, value);
    return acc;
  }, new Map<string, string>());

const integrationCategoryKey = (integration: IPOSIntegration): string => `${integration.type}|${[...(integration.categories || [])].sort().join(',')}`;

/**
 * Pairs snapshot integrations with current ones by `type` — an integration deleted and re-added
 * during POS recovery keeps its type but gets a new `integration_id`, so matching on id would lose
 * the comparison entirely.
 *
 * Two passes within each type: `categories` first, then any remaining same-type entries in order. The
 * second pass matters when a site splits one record into separate labor and ticket records (or merges
 * them back) — the categories stop lining up, but it is still the same POS, and pairing shows the
 * config keys that actually changed instead of a wholesale, unreplayable "integration removed".
 */
const pairIntegrations = (original: IPOSIntegration[], current: IPOSIntegration[]): { pairs: [IPOSIntegration, IPOSIntegration][]; unmatchedOriginal: IPOSIntegration[]; unmatchedCurrent: IPOSIntegration[] } => {
  const originalByType = groupBy(original, ({ type }) => String(type));
  const currentByType = groupBy(current, ({ type }) => String(type));
  const pairs: [IPOSIntegration, IPOSIntegration][] = [];
  const matchedOriginal = new Set<IPOSIntegration>();
  const matchedCurrent = new Set<IPOSIntegration>();

  originalByType.forEach((originalGroup, type) => {
    const currentGroup = currentByType.get(type) || [];
    const takeMatch = (originalIntegration: IPOSIntegration, candidates: IPOSIntegration[]) => {
      if (matchedOriginal.has(originalIntegration)) return;
      const match = candidates.find((candidate) => !matchedCurrent.has(candidate));
      if (!match) return;
      pairs.push([originalIntegration, match]);
      matchedOriginal.add(originalIntegration);
      matchedCurrent.add(match);
    };

    const currentByCategories = groupBy(currentGroup, integrationCategoryKey);
    originalGroup.forEach((originalIntegration) => takeMatch(originalIntegration, currentByCategories.get(integrationCategoryKey(originalIntegration)) || []));
    originalGroup.forEach((originalIntegration) => takeMatch(originalIntegration, currentGroup));
  });

  return {
    pairs,
    unmatchedOriginal: original.filter((integration) => !matchedOriginal.has(integration)),
    unmatchedCurrent: current.filter((integration) => !matchedCurrent.has(integration)),
  };
};

/**
 * Compares integration public config as a keyed set. `updated_at` takes no part — it flips on a
 * credential rotation that cannot affect a calculation.
 *
 * A config key present in the snapshot but gone from the integration is reported yet **cannot** be
 * restored: `applyConfigOverride` in `integrations_service` ignores override keys absent from the
 * stored config, so an original-settings replay uses the adapter's current behavior for that key.
 */
const diffIntegrations = (original: IPOSIntegration[], current: IPOSIntegration[]): ISettingDifference[] => {
  const { pairs, unmatchedOriginal, unmatchedCurrent } = pairIntegrations(original, current);

  const configDifferences = pairs.flatMap(([originalIntegration, currentIntegration]) => {
    const originalConfig = publicConfigMap(originalIntegration.config);
    const currentConfig = publicConfigMap(currentIntegration.config);

    return Array.from(new Set([...originalConfig.keys(), ...currentConfig.keys()]))
      .sort()
      .reduce<ISettingDifference[]>((acc, key) => {
        // Paired integrations share a type today, so each branch still names its own side — the labels
        // stay right if pairing ever crosses types.
        const base = { setting: CalculatorSettingKey.INTEGRATION_CONFIG, label: `${currentIntegration.type} · ${key}` };

        if (!currentConfig.has(key)) return [...acc, { ...base, label: `${originalIntegration.type} · ${key}`, changeType: SettingChangeType.REMOVED, original: originalConfig.get(key), replayable: false }];
        if (!originalConfig.has(key)) return [...acc, { ...base, changeType: SettingChangeType.ADDED, current: currentConfig.get(key), replayable: true }];
        if (originalConfig.get(key) === currentConfig.get(key)) return acc;

        return [...acc, { ...base, changeType: SettingChangeType.MODIFIED, original: originalConfig.get(key), current: currentConfig.get(key), replayable: true }];
      }, []);
  });

  // An unmatchable snapshot entry means the integration type itself changed — the site moved POS.
  // Reported as a change rather than silently skipped, and not replayable: there is nothing to overlay onto.
  const removedIntegrations = unmatchedOriginal.map(
    (integration): ISettingDifference => ({
      setting: CalculatorSettingKey.INTEGRATION_CONFIG,
      changeType: SettingChangeType.REMOVED,
      label: String(integration.type),
      original: Object.fromEntries(publicConfigMap(integration.config)),
      replayable: false,
    })
  );
  const addedIntegrations = unmatchedCurrent.map(
    (integration): ISettingDifference => ({
      setting: CalculatorSettingKey.INTEGRATION_CONFIG,
      changeType: SettingChangeType.ADDED,
      label: String(integration.type),
      current: Object.fromEntries(publicConfigMap(integration.config)),
      replayable: true,
    })
  );

  return [...configDifferences, ...removedIntegrations, ...addedIntegrations];
};

const diffScalar = (setting: CalculatorSettingKey, label: string, original: unknown, current: unknown): ISettingDifference[] =>
  original === current ? [] : [{ setting, changeType: SettingChangeType.MODIFIED, label, original, current, replayable: true }];

/**
 * The snapshot presence sweep. Every payment created before day segments were captured fails it, by
 * design — its settings cannot be reconstructed. Adding a tenth calculator setting later means
 * adding it here in lockstep, or stale snapshots will report as complete.
 *
 * Presence is checked at the top level only. A partially populated nested object — a grace period policy
 * stored before it grew a field — is defaulted during the comparison instead: it is still comparable, and
 * failing it would send the manager down the "current settings" path rather than giving them an answer.
 */
const getMissingSnapshotFields = (snapshot: IPaymentSetCalculations): string[] => {
  const { gracePeriodPolicy, businessDayStart, timezone, integrations, creditCardPercentFee, daySegments } = snapshot?.settings || ({} as IPaymentSetCalculationsSettings);

  return [
    ...(gracePeriodPolicy ? [] : ['gracePeriodPolicy']),
    ...(businessDayStart ? [] : ['businessDayStart']),
    ...(timezone ? [] : ['timezone']),
    ...(integrations ? [] : ['integrations']),
    ...(typeof creditCardPercentFee === 'number' ? [] : ['creditCardPercentFee']),
    ...(daySegments ? [] : ['daySegments']),
    // Rules, sales groups and participants are read off results[0]; without it there is nothing to compare them to.
    ...(snapshot?.results?.length ? [] : ['results']),
  ];
};

/**
 * Reports whether a payment's stored calculator settings are complete, whether they differ from the
 * site's current settings, and exactly which fields differ.
 *
 * `changed` is derived from `differences.length`, never computed separately, so the settings gate and
 * the diff can never disagree. When the snapshot is incomplete the comparison short-circuits:
 * `differences` is empty and `missingFields` explains why, because comparing against a partial
 * snapshot produces differences that mean nothing.
 *
 * @param snapshot - The payment's stored `calculations` — `settings` plus `results[]`, captured at payment create.
 * @param current - The site's live calculator settings, as held in Redux when the gate renders.
 * @param businessDate - The payment's business date (`YYYY-MM-DD`). Selects which of the site's day segments applied.
 * @returns The comparison result: completeness, whether anything changed, and the per-field differences.
 */
export const compareCalculatorSettings = (snapshot: IPaymentSetCalculations, current: ICurrentCalculatorSettings, businessDate: string): ISettingsComparison => {
  /**
   * Both exits build their result here, so `changed` and `fullyReplayable` always come from the same
   * differences. The incomplete exit passes an empty list, which makes `fullyReplayable` vacuously true —
   * hence the "read it only when `changed`" note on the type.
   */
  const comparison = (complete: boolean, missingFields: string[], differences: ISettingDifference[]): ISettingsComparison => ({
    complete,
    changed: differences.length > 0,
    fullyReplayable: differences.every(({ replayable }) => replayable),
    missingFields,
    differences,
  });

  const missingFields = getMissingSnapshotFields(snapshot);
  if (missingFields.length) return comparison(false, missingFields, []);

  const { settings } = snapshot;
  // Every element of results[] carries the same full rules array, so results[0] is the canonical source.
  const [snapshotResult] = snapshot.results;
  const { calculatorRules, siteSettings } = current;

  const [snapshotDefaultRules, snapshotTipShares] = partition(snapshotResult.rules || [], (rule) => Boolean(rule.default));
  const [currentDefaultRules, currentTipShares] = partition(calculatorRules?.rules || [], (rule) => Boolean(rule.default));

  // Per field rather than an object spread: a spread lets an explicit `null` from either side
  // overwrite the default and report a difference that is really an unset value.
  const gracePeriod = (policy?: IDaySegmentGracePeriodPolicy): IDaySegmentGracePeriodPolicy => ({
    segmentStart: policy?.segmentStart ?? DEFAULT_GRACE_PERIOD_POLICY.segmentStart,
    segmentEnd: policy?.segmentEnd ?? DEFAULT_GRACE_PERIOD_POLICY.segmentEnd,
    timeWorked: policy?.timeWorked ?? DEFAULT_GRACE_PERIOD_POLICY.timeWorked,
  });
  const snapshotGracePeriod = gracePeriod(settings.gracePeriodPolicy);
  const currentGracePeriod = gracePeriod(siteSettings?.gracePeriodPolicy);

  const differences: ISettingDifference[] = [
    ...diffByKey<ICalculatorRule>({
      setting: CalculatorSettingKey.TIP_SHARES,
      original: snapshotTipShares,
      current: currentTipShares,
      key: ruleContributorKey,
      label: (rule) => rule.contributor?.name || 'Tip share',
      value: (rules) => rules.map(pickComparedRuleFields),
    }),
    // The default pool is a single logical setting, so its rules compare as one unit.
    ...diffByKey<ICalculatorRule>({
      setting: CalculatorSettingKey.DEFAULT_POOL,
      original: snapshotDefaultRules,
      current: currentDefaultRules,
      key: () => 'default',
      label: () => 'Default pool',
      value: (rules) => rules.map(pickComparedRuleFields),
    }),
    ...diffByKey<ICategoryGroup>({
      setting: CalculatorSettingKey.SALES_GROUPS,
      original: snapshotResult.meta?.categoryGroups || [],
      current: calculatorRules?.categoryGroups || [],
      key: ({ name }) => name,
      label: ({ name }) => name,
      value: (groups) => groups.map(({ categories }) => [...(categories || [])].sort()),
    }),
    // `meta.participants` is only captured when a rule benefits a participant, so an absent snapshot
    // value is ambiguous. Skip participants entirely in that case and rely on the rules diff — a
    // participant no rule references changes no output.
    ...(snapshotResult.meta?.participants?.length
      ? diffByKey<ICalculatorParticipant>({
          setting: CalculatorSettingKey.PARTICIPANTS,
          original: snapshotResult.meta.participants,
          current: calculatorRules?.participants || [],
          key: ({ type, name }) => `${type}:${name}`,
          label: ({ name }) => name,
        })
      : []),
    ...diffScalar(CalculatorSettingKey.GRACE_PERIOD, 'Grace period start', snapshotGracePeriod.segmentStart, currentGracePeriod.segmentStart),
    ...diffScalar(CalculatorSettingKey.GRACE_PERIOD, 'Grace period end', snapshotGracePeriod.segmentEnd, currentGracePeriod.segmentEnd),
    ...diffScalar(CalculatorSettingKey.GRACE_PERIOD, 'Grace period time worked', snapshotGracePeriod.timeWorked, currentGracePeriod.timeWorked),
    // Different field names on the two sides. Defaulted before `Number()`, not coerced by it alone:
    // `Number(undefined)` is NaN, which equals nothing and would report a difference reading as NaN.
    ...diffScalar(
      CalculatorSettingKey.CREDIT_CARD_FEE,
      'Credit card fee',
      Number(settings.creditCardPercentFee ?? appConfig.TIP_CALCULATOR.DEFAULT_PERCENT_FEE),
      Number(siteSettings?.tipCalculatorPercentFee ?? appConfig.TIP_CALCULATOR.DEFAULT_PERCENT_FEE)
    ),
    ...diffScalar(CalculatorSettingKey.BUSINESS_DAY, 'Business day start', settings.businessDayStart, siteSettings?.businessDayStart),
    ...diffScalar(CalculatorSettingKey.BUSINESS_DAY, 'Timezone', settings.timezone, siteSettings?.timezone),
    ...diffDaySegments(settings.daySegments || [], daySegmentsForDate(current.daySegments || [], businessDate)),
    ...diffIntegrations(settings.integrations || [], current.integrations || []),
  ];

  return comparison(true, missingFields, differences);
};

/**
 * Maps a comparison onto the state the manager is shown.
 *
 * An absent comparison — no stored calculations, or the fetch failed — lands on the same informational
 * state as an incomplete snapshot: the calculator proceeds with current settings and says so.
 */
export const resolveSettingsGateState = (comparison?: ISettingsComparison): SettingsGateState => {
  if (!comparison?.complete) return SettingsGateState.INCOMPLETE;
  return comparison.changed ? SettingsGateState.CHANGED : SettingsGateState.UNCHANGED;
};

/**
 * Opt-in switch for production. Set `localStorage.setItem('kf:calculatorSettingsDebug', 'true')` in the
 * browser console to trace a specific site's gate without a build.
 */
export const SETTINGS_DEBUG_STORAGE_KEY = 'kf:calculatorSettingsDebug';

interface ISettingsComparisonContext {
  paymentId: string;
  date: string;
}

/**
 * Off for ordinary users. On outside production, or anywhere the operator opts in explicitly.
 *
 * Note this cannot key off `NODE_ENV`: CRA builds every environment with `NODE_ENV=production`, so
 * staging — where QA needs this most — would look identical to production.
 */
export const isSettingsDebugEnabled = (): boolean => {
  try {
    if (window.localStorage.getItem(SETTINGS_DEBUG_STORAGE_KEY) === 'true') return true;
  } catch {
    // Storage access throws in some privacy modes; fall through to the environment check.
  }

  return !isProduction();
};

/**
 * Writes a settings comparison to the browser console, and nowhere else — never to a backend, never
 * persisted with the payment. Without it, "why did it say settings changed?" needs a debugger.
 *
 * Only the differences are logged, never the settings objects: dumping a full snapshot would put a
 * site's entire calculator configuration somewhere screenshots get taken.
 */
export const logSettingsComparison = (comparison: ISettingsComparison, context: ISettingsComparisonContext): void => {
  if (!isSettingsDebugEnabled()) return;

  // The gate state itself, so the console line and the modal the manager saw can never disagree.
  const outcome = resolveSettingsGateState(comparison).toUpperCase();

  /* eslint-disable no-console */
  console.groupCollapsed(`[calculator] settings ${outcome} — ${context.date} (payment ${context.paymentId})`);
  if (!comparison.complete) console.info('missing snapshot fields:', comparison.missingFields);
  if (comparison.differences.length) {
    console.table(comparison.differences);
    if (!comparison.fullyReplayable) console.info('some differences cannot be restored by an original-settings replay');
  }
  console.groupEnd();
  /* eslint-enable no-console */
};

/**
 * A stored snapshot as the override bundle the POS POST accepts — the inverse of `buildPaymentSetCalculations`.
 *
 * Absent fields are left out rather than defaulted, so the server falls back per field to live config; a guessed
 * value would replay a setting that was never in force.
 */
export const buildSettingsOverride = (snapshot?: IPaymentSetCalculations): ICalculatorSettingsOverride | undefined => {
  if (!snapshot?.settings) return undefined;

  const { gracePeriodPolicy, businessDayStart, timezone, creditCardPercentFee, daySegments, integrations } = snapshot.settings;
  const [result] = snapshot.results || [];

  return {
    ...(gracePeriodPolicy && { gracePeriodPolicy }),
    ...(businessDayStart && { businessDayStart }),
    ...(timezone && { timezone }),
    // Guarded on type rather than truthiness: zero is a real fee.
    ...(typeof creditCardPercentFee === 'number' && { creditCardPercentFee }),
    ...(daySegments?.length && { daySegments }),
    ...(integrations?.length && { integrations }),
    ...(result?.rules?.length && { rules: result.rules }),
    ...(result?.meta?.categoryGroups?.length && { categoryGroups: result.meta.categoryGroups }),
    // Only captured when a rule benefited a participant, so absence is normal rather than an error.
    ...(result?.meta?.participants?.length && { participants: result.meta.participants }),
  };
};

/** The calculation inputs a replayed run swaps in, in place of the site's live rules and segments. */
export interface IReplayInputs {
  rules: ICalculatorRulesSource;
  daySegments: IDaySegment[];
}

/**
 * The snapshot's windows, keyed to the ids the override POST assigned — matched on name, the only field common
 * to both sides. Without this, `getSortedUniqueSegments` finds no match and the per-segment requests go out with
 * `--:--` windows, which the calculator echoes into results that can be saved with a payment.
 */
const replayDaySegments = (posResults: IPOSResults, snapshotSegments: IPaymentSetDaySegment[] = []): IDaySegment[] => {
  const windowsByName = new Map(snapshotSegments.map((segment) => [segment.name, segment]));
  const seenIds = new Set<string>();

  return (posResults?.data || []).flatMap(({ segments }) =>
    (segments || []).reduce<IDaySegment[]>((acc, segment) => {
      if (!segment?.id || seenIds.has(segment.id)) return acc;
      seenIds.add(segment.id);

      const window = windowsByName.get(segment.name);
      if (!window) return acc;

      return [...acc, { id: segment.id, name: segment.name, start: window.start, end: window.end, endsNextDay: window.endsNextDay } as IDaySegment];
    }, [])
  );
};

/**
 * What a replayed run calculates with, both needed together or not at all. Undefined when the snapshot recorded
 * no rules, which leaves the site's live rules and segments in use.
 *
 * @param posResults - POS data returned by the override POST.
 */
export const buildReplayInputs = (snapshot: IPaymentSetCalculations | undefined, posResults: IPOSResults): IReplayInputs | undefined => {
  const [result] = snapshot?.results || [];
  if (!result?.rules?.length) return undefined;

  return {
    // Participants are whatever the snapshot captured, which may be nothing.
    rules: { rules: result.rules, categoryGroups: result.meta?.categoryGroups || [], participants: result.meta?.participants || [] },
    daySegments: replayDaySegments(posResults, snapshot?.settings?.daySegments),
  };
};

/**
 * Site integrations in the shape the comparison expects.
 * `GET /v1/sites/:id/integrations` carries the same public config as the POS settings block the snapshot was taken
 * from, which is what lets the comparison run before any POS request is made.
 */
export const buildCurrentPOSIntegrations = (integrations: IIntegration[] = []): IPOSIntegration[] => integrations.map(({ id, type, updatedAt, categories, config }) => ({ integrationId: id, type, updatedAt, categories, config }));
